// List of paths that vite-react-ssg should statically prerender at build time.
// Keep in sync with the sitemap edge function (supabase/functions/sitemap).
//
// Postal-code drilldown paths (~2050 unique paths × 5 langs = ~10K) are
// generated from src/generated/postal-snapshot.json, which the prebuild
// script (scripts/build-postal-snapshot.ts) fills with a fresh dump of the
// postal_areas table from Supabase.
//
// Everything else (shop products, news articles, admin) is dynamic and falls
// back to client-side rendering.
// Relative import (not @/) because this module is read by vite.config.ts
// at Node load-time, before Vite's path aliases are wired up.
import {
  listCounties,
  listMunicipalities,
  listLocalities,
  getCountyByCode,
  getMunicipalityByCode,
} from './lib/postal-snapshot';

const LANGS = ['sv', 'en', 'da', 'fi', 'no'];

const STATIC_PATHS_PER_LANG = [
  '', // homepage
  '/datafiler',
  '/shop',
  '/om-oss',
  '/kontakt',
  '/geposit',
  '/information/data-i-vara-tjanster',
  '/information/postnummer',
  '/information/kopvillkor',
  '/information/faq',
  '/information/nyheter',
  '/integritetspolicy',
  '/cookiepolicy',
];

function buildPostalPaths(): string[] {
  const paths: string[] = [];

  for (const county of listCounties()) {
    if (!county.slug) continue;
    paths.push(`/information/postnummer/${county.slug}`);
  }

  for (const muni of listMunicipalities()) {
    if (!muni.slug || !muni.parent_code) continue;
    const county = getCountyByCode(muni.parent_code);
    if (!county?.slug) continue;
    paths.push(`/information/postnummer/${county.slug}/${muni.slug}`);
  }

  for (const locality of listLocalities()) {
    if (!locality.slug || !locality.parent_code) continue;
    const muni = getMunicipalityByCode(locality.parent_code);
    if (!muni?.slug || !muni.parent_code) continue;
    const county = getCountyByCode(muni.parent_code);
    if (!county?.slug) continue;
    paths.push(
      `/information/postnummer/${county.slug}/${muni.slug}/${locality.slug}`
    );
  }

  return paths;
}

const POSTAL_PATHS = buildPostalPaths();

export const PRERENDERED_PATHS: string[] = [
  // Root: produces dist/index.html, which the GCS load balancer rewrites
  // 404s to (so client-side routing keeps working for non-prerendered
  // dynamic paths like /sv/shop/123).
  '/',
  ...LANGS.flatMap((lang) => [
    ...STATIC_PATHS_PER_LANG.map((suffix) => `/${lang}${suffix}`),
    ...POSTAL_PATHS.map((suffix) => `/${lang}${suffix}`),
  ]),
];
